import { useForm } from '@tanstack/react-form';
import { useState } from 'react';
import { z } from 'zod';

import type { ListCustomers } from '@cold-ambulance/client';
import { Button, DialogFooter, Input, Label } from '@cold-ambulance/shadcn';
import {
  FieldMessage,
  FormMessage,
  errorToFormIssue,
  useAction,
} from '@cold-ambulance/ui';

import { CreateDialog } from '../../components/create-dialog.tsx';

const blankable = z.string().min(1).or(z.literal(''));
const optionalCr = z
  .string()
  .regex(/^\d{10}$/, {
    message: 'رقم السجل التجاري يجب أن يكون 10 أرقام',
  })
  .or(z.literal(''));

const schema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email format' }),
  name: z.string().min(1, { message: 'Name is required' }),
  address: blankable,
  commercialRegistrationNumber: optionalCr,
});

export function UpdateCustomerDialog({
  customer,
  trigger,
}: {
  customer: ListCustomers['records'][number];
  trigger: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const updateAction = useAction('PATCH /customer/{id}', {
    invalidate: ['GET /customers'],
  });

  const form = useForm({
    defaultValues: {
      email: customer.email,
      name: customer.name,
      address: customer.address ?? '',
      commercialRegistrationNumber:
        customer.commercialRegistrationNumber ?? '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await updateAction.mutateAsync({
            id: customer.id,
            email: value.email,
            name: value.name,
            address: value.address,
            commercialRegistrationNumber: value.commercialRegistrationNumber,
          });
          setOpen(false);
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <CreateDialog
      form={form}
      open={open}
      onOpenChange={setOpen}
      title="Edit Customer"
      subtitle="Update customer information and legal data extracted from documents"
      trigger={trigger}
    >
      <form.Field
        name="email"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="customer@example.com"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="name"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="name">اسم العميل</Label>
            <Input
              id="name"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <div className="border-border/60 mt-4 border-t pt-4">
        <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          البيانات المستخرجة من المستندات
        </h3>
      </div>

      <form.Field
        name="address"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="address">العنوان الوطني</Label>
            <Input
              id="address"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="commercialRegistrationNumber"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="commercialRegistrationNumber">
              رقم السجل التجاري (10 أرقام)
            </Label>
            <Input
              id="commercialRegistrationNumber"
              type="text"
              inputMode="numeric"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="4030876543"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Subscribe
        selector={(state) => state.errors}
        children={(errors) => <FormMessage errors={errors} />}
      />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button type="submit" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update'}
            </Button>
          )}
        />
      </DialogFooter>
    </CreateDialog>
  );
}
